<template>
  <div class="flex h-full w-full flex-col justify-between overflow-y-auto">
    <div class="min-h-0 flex-grow overflow-y-auto">
      <div class="flex min-h-full flex-col justify-center py-4">
        <h1
          class="gc-font-title mb-10 mt-5 text-center font-text-pop-title text-text-pop-title">
          {{ signUpTitle }}
        </h1>
        <div
          v-if="props.recovery && props.recovery.message_text"
          class="font-primary mb-6 text-center text-small-titles md:mb-14">
          {{ props.recovery?.message_text }}
        </div>
        <CustomInput
          id="name"
          customDivClass="mb-4 w-full text-xs"
          customLabelClass="text-small-titles font-primary"
          customInputClass="w-full mt-1 h-[46px]"
          :label="$t('general.name')"
          :maxLength="100"
          :hasError="nameWarning"
          autoComplete="name"
          @update:text="fillName"
          @blur="handleNameBlur" />
        <CustomInput
          id="signUpemail"
          customDivClass="mb-4 text-xs w-full"
          customLabelClass="text-small-titles font-primary"
          customInputClass="w-full mt-1 h-[46px]"
          :label="$t('general.email')"
          :maxLength="255"
          :hasError="emailWarning"
          autoComplete="email"
          @update:text="fillEmail"
          @blur="handleEmailBlur" />
        <CustomSelect
          iconClass="mr-3 w-6 h-6"
          :containerClass="['signUpCountry', countryWarning ? 'warning' : '']"
          :title="$t('general.country')"
          :options="countryList"
          :searchable="true"
          label="label"
          labelClass="!font-normal !mb-1 !text-xs text-small-titles mb-0 font-primary"
          optionClass="ml-0"
          :selectedObject="selectedCountryObject"
          @changedOption="handleCountryChange"
          @onMultiselectClose="onMultiselectClose" />

        <div
          class="mt-10 flex justify-center"
          v-if="!props.recovery?.message_title">
          <div class="font-primary text-sm">
            {{ $t('general.alreadyMember') }} ?
            <a
              href="#"
              class="cursor-pointer text-brand underline"
              @click="goToLogin">
              {{ $t('general.login') }}
            </a>
          </div>
        </div>

        <div
          class="font-primary mb-2 mt-10 flex items-center"
          :class="{ warning: termsWarning }">
          <input
            id="accept-terms"
            name="accept-terms"
            type="checkbox"
            v-model="acceptTerms"
            @change="handleTermsChange"
            class="mr-2" />
          <label class="underline-on-hover text-xs" @click.stop="viewPolicies">
            {{ $t('GDPRShortConsentText') }}
          </label>
        </div>
      </div>
    </div>

    <div class="my-5 mt-10 flex justify-around gap-2">
      <CustomButton
        v-if="isMobile && props.recovery"
        class="backButton"
        width="w-full"
        :label="$t('general.back')"
        icon="chevron-left"
        customClass="secondary"
        @click="emit('close')" />
      <CustomButton
        class="signUpButton"
        width="w-full"
        :label="signUpButtonLabel"
        @click="doSignUp" />
    </div>
    <InformativeModal
      :isVisible="showInvalidEmail"
      :title="$t('general.emailAlreadyExists')"
      :description="$t('general.emailInUse')"
      :buttonText="$t('general.closeButtonText')"
      type="warning"
      @update:isVisible="onInvalidMailModalClose" />
  </div>

  <CustomDrawer
    v-model="policiesVisible"
    :position="isMobile ? 'bottom' : 'right'"
    :width="isMobile ? 'w-full' : 'w-[35%]'"
    height="h-auto">
    <template #drawerContent>
      <GDPRPolicyText @close="policiesVisible = false" />
    </template>
  </CustomDrawer>
</template>

<script setup>
import {
  ref,
  defineAsyncComponent,
  inject,
  computed,
  watch,
  onMounted,
  onUnmounted,
} from 'vue';
import { useUserProfile } from '@stores/userProfile.store';
import CustomInput from '@/components/customComponents/CustomInput.vue';
import CustomButton from '@/components/customComponents/CustomButton.vue';
import CustomSelect from '@/components/customComponents/CustomSelect.vue';
import GDPRPolicyText from '@/components/PersonalData/GDPRPolicyText.vue';
import InformativeModal from '@/components/customComponents/InformativeModal.vue';
import { useShoppingRecoveryStore } from '@stores/shoppingRecovery.store';
import { useScreenSize } from '@composables/useScreenSize';
import { validateName, validateEmail } from '@/utils/inputValidation';
import { useI18n } from 'vue-i18n';
import { withLocalizedCountryLabels } from '@/utils/countryDisplayNames';

const userProfile = useUserProfile();
const shoppingRecoveryStore = useShoppingRecoveryStore();
const { sendRecoveryPromotionForm } = shoppingRecoveryStore;

const { t, locale } = useI18n();

const props = defineProps({
  recovery: {
    type: Object,
    required: false,
  },
});

const CustomDrawer = defineAsyncComponent(
  () => import('@/components/customComponents/CustomDrawer.vue'),
);

const emit = defineEmits([
  'backToLogin',
  'showSignupModal',
  'isSignUpEmptyOrValid',
  'close',
]);

const { isMobile } = useScreenSize();

const policiesVisible = ref(false);
const acceptTerms = ref(false);
const applicationSetup = inject('applicationSetup');

const name = ref('');
const email = ref('');

const nameWarning = ref(false);
const emailWarning = ref(false);
const countryWarning = ref(false);
const termsWarning = ref(false);

const showInvalidEmail = ref(false);

const selectedCountryObject = ref(null);

const signUpTitle = computed(() => {
  return props.recovery?.message_title || t('loyaltyButtonLabel');
});

const signUpButtonLabel = computed(() => {
  return props.recovery ? t('general.submit') : t('general.signUp');
});

const handleKeydown = (event) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    doSignUp();
  }
};

const applySetupDefaultCountry = () => {
  selectedCountryObject.value = countryList.value[0];
  const code = (applicationSetup.value?.default_country || '').toLowerCase();
  const country = countryList.value.find((c) => c.value === code);
  if (country) {
    selectedCountryObject.value = country;
  }
};

onMounted(() => {
  applySetupDefaultCountry();
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});

const onMultiselectClose = () => {
  const selectedOption = document.querySelector(
    '.signUpCountry .multiselect__option--highlight span',
  );
  if (selectedOption) {
    const selectedText = selectedOption.textContent.trim();
    selectedCountryObject.value = countryList.value.find(
      (country) => country.label.toLowerCase() === selectedText.toLowerCase(),
    );
    countryWarning.value = false;
  }
};

const onInvalidMailModalClose = () => {
  emit('backToLogin');
  showInvalidEmail.value = false;
};

const goToLogin = () => {
  emit('backToLogin');
};

const validateFields = () => {
  let isValid = true;

  if (!name.value) {
    nameWarning.value = true;
    isValid = false;
  } else if (!validateName(name.value).isValid) {
    nameWarning.value = true;
    isValid = false;
  } else {
    nameWarning.value = false;
  }

  if (!email.value) {
    emailWarning.value = true;
    isValid = false;
  } else if (!validateEmail(email.value).isValid) {
    emailWarning.value = true;
    isValid = false;
  } else {
    emailWarning.value = false;
  }

  if (selectedCountryObject.value.value === '') {
    countryWarning.value = true;
    isValid = false;
  } else {
    countryWarning.value = false;
  }

  if (!acceptTerms.value) {
    termsWarning.value = true;
    isValid = false;
  } else {
    termsWarning.value = false;
  }

  return isValid;
};

const doSignUp = async () => {
  if (!name.value && !email.value && selectedCountryObject.value.value === '') {
    policiesVisible.value = false;
    emit('close');
    return;
  }

  const valid = validateFields();
  if (!valid) {
    return;
  }

  const userData = {
    name: name.value,
    login: email.value,
    language: applicationSetup.value.language,
    country_code: selectedCountryObject.value.value,
  };

  try {
    if (!props.recovery) {
      const response = await userProfile.doSignUp(userData);

      if (response.data.already_registered) {
        showInvalidEmail.value = true;
        return;
      }

      emit('showSignupModal');
    } else {
      userData.frequency = props.recovery.frequency;
      userData.context = props.recovery.context || 'BOOKINGENGINE';
      userData.trigger = props.recovery.trigger;
      userData.email = userData.login;

      await sendRecoveryPromotionForm(userData).then((response) => {
        if (response && response.data.status) {
          emit('showSignupModal');
        }
      });
    }
  } catch (error) {
    console.error('Sign up failed:', error);
  }
};

const viewPolicies = () => {
  policiesVisible.value = true;
};

const fillName = (value) => {
  // Trim leading/trailing spaces (per AC requirements)
  name.value = value.trim();
  // Only clear error when typing (validation happens onBlur)
  if (nameWarning.value) {
    nameWarning.value = false;
  }
  emit('isSignUpEmptyOrValid', checkFormValidity());
};

const handleNameBlur = () => {
  // Reuse existing validation logic on blur
  if (!name.value) {
    nameWarning.value = true;
  } else {
    const validation = validateName(name.value);
    nameWarning.value = !validation.isValid;
  }
};

const fillEmail = (value) => {
  email.value = value;
  // Only clear error when typing (validation happens onBlur)
  if (emailWarning.value) {
    emailWarning.value = false;
  }
  emit('isSignUpEmptyOrValid', checkFormValidity());
};

const handleEmailBlur = () => {
  // Reuse existing validation logic on blur
  if (!email.value) {
    emailWarning.value = false; // Empty allowed (user can leave empty)
  } else {
    const validation = validateEmail(email.value);
    emailWarning.value = !validation.isValid;
  }
};

const handleCountryChange = (selectedCountry) => {
  selectedCountryObject.value = selectedCountry;
  countryWarning.value = false;
  emit('isSignUpEmptyOrValid', checkFormValidity());
};

const handleTermsChange = () => {
  termsWarning.value = !acceptTerms.value;
  emit('isSignUpEmptyOrValid', checkFormValidity());
};

const checkFormValidity = () => {
  const isEmpty =
    !name.value &&
    !email.value &&
    selectedCountryObject.value?.value === '' &&
    !acceptTerms.value;
  const isValid =
    name.value &&
    validateName(name.value).isValid &&
    email.value &&
    validateEmail(email.value).isValid &&
    selectedCountryObject.value?.value !== '' &&
    acceptTerms.value;
  return isEmpty || isValid;
};

const countryList = computed(() => {
  const defaultCountry = {
    value: '',
    label: t('general.selectYourCountry'),
    countryCode: '+1',
  };

  let countries = applicationSetup.value.countries ?? [];

  // Remove any existing default country entries
  countries = countries.filter(
    (country) =>
      country.value !== defaultCountry.value &&
      country.label !== defaultCountry.label,
  );

  countries = withLocalizedCountryLabels(countries, locale.value);

  // Add the default country at the beginning
  countries.unshift(defaultCountry);

  return countries;
});

watch(
  () => applicationSetup.value.language,
  () => {
    applySetupDefaultCountry();
  },
  { deep: true },
);
</script>
<style scoped>
.underline-on-hover:hover {
  @apply cursor-pointer underline;
}

:deep(.multiselect__tags) {
  @apply h-[46px];
  @apply flex items-center py-0;
}

:deep(.multiselect__select) {
  @apply py-0;
}

:deep(.multiselect__single > div > span) {
  @apply text-sm;
}

.warning input {
  @apply border-error text-error;
}

.warning input::placeholder {
  @apply text-error;
}

.warning label {
  @apply text-error;
}

.warning input[type='checkbox'] {
  @apply accent-error;
}
</style>
